import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import EditIcon from '@mui/icons-material/Edit';
import TableSortLabel from '@mui/material/TableSortLabel';
import { styled } from '@mui/system';
import { visuallyHidden } from '@mui/utils';
import { map, get, includes, filter, isEmpty, size } from 'lodash';
import { useParams, useSearchParams} from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PRIVILEGES, useAuth } from 'app/auth.ctx';

import { useGetListsQuery } from 'app/apis/lists.api';


const HeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  textTransform: 'uppercase'
}));

const SortableHeaderCell = ({ field, label, sortInfo, onSortChange }) => {
  const orderBy = get(sortInfo, 'orderBy');
  const order = get(sortInfo, 'order');
  return (
    <HeaderCell>
      <TableSortLabel
        active={orderBy === field}
        direction={orderBy === field ? order : 'asc'}
        onClick={() => onSortChange(field)}
      >
        {label}
        {orderBy === field ? (
          <Box component="span" sx={visuallyHidden}>
            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
          </Box>
        ) : null}
      </TableSortLabel>
    </HeaderCell>
  )
};

const ExcerptDiv = styled('div')(({ theme }) => ({
  color: '#4b9ff0',
  marginLeft: 20,
  marginTop: 10
}));

const sx = {
  selectedRow: {
    backgroundColor: '#91a8d02e'
  }
};

const InstrumentsTableRow = React.memo(({
                                          instrument,
                                          selected,
                                          onSelectChange,
                                          canEdit,
                                          showAdoptionDate,
                                          showAbrogationDate,
                                          showLastAmendmentDate,
                                          showEntryInForceDate,
                                          showMonitoringDate,
                                          hideInPrint,
                                          lang,
                                        }) => {
  const instrumentText = instrument['title-highlight'] || instrument.title;
  return (
    <TableRow
      sx={selected ? sx.selectedRow : {}}
      className={hideInPrint ? 'hidePrint' : ''}
    >
      <TableCell className="hidePrint" padding="checkbox">
        <Checkbox
          color="primary"
          checked={selected}
          onChange={e => onSelectChange(instrument.key, e.target.checked)}
        />
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={1}>
          <Link
            underline="hover"
            href={`/${lang}/instruments/${instrument.key.replace(/\//g, '-')}`}
          >
            {instrument.key}
          </Link>
          {canEdit && (
            <a
              href={`/${lang}/instruments/${instrument.id}/edit`}
              className={'hidePrint'}
            >
              <EditIcon sx={{ fontSize: 14, cursor: "pointer" }}/>
            </a>
          )}
        </Stack>
      </TableCell>
      <TableCell>
        <div
          dangerouslySetInnerHTML={{ __html: instrumentText }}
        />
        {instrument.excerpt && (
          <ExcerptDiv dangerouslySetInnerHTML={{ __html: instrument.excerpt }}/>
        )}
      </TableCell>
      {showAdoptionDate && (
        <TableCell>
          {instrument.adoptionDate_display}
        </TableCell>
      )}
      {showAbrogationDate && (
        <TableCell>
          {instrument.abrogationDate_display}
        </TableCell>
      )}
      {showEntryInForceDate && (
        <TableCell>
          {instrument.inForceDate_display}
        </TableCell>
      )}
      {showLastAmendmentDate && (
        <TableCell>
          {instrument.lastAmendmentDate_display}
        </TableCell>
      )}
      {showMonitoringDate && (
        <TableCell>
          {instrument.monitoringDate_display}
        </TableCell>
      )}
      <TableCell>
        {get(instrument, 'status.name')}
      </TableCell>
    </TableRow>
  );
});

const InstrumentsTableHeader = (props) => {
  const { t } = useTranslation();
  const {
    showAdoptionDate, showAbrogationDate, showLastAmendmentDate, showEntryInForceDate, showMonitoringDate,
    allSelected, someSelected, onSelectAllChange, sortInfo, onSortChange
  } = props;

  const { lang} = useParams();
  const { data: instrumentList } = useGetListsQuery({ lang });
  //const {instrumentKeys} = useInstrument();
  const [searchParams] = useSearchParams();
  const tagIds = searchParams.get('tagIds')?.split(',') ?? [];

  const tagResult = (instrumentList?.tags || []).filter(tag => tagIds.includes(String(tag.id)));
  const uniqueTagResult = [...new Set(tagResult)];

  const tagIdsString = uniqueTagResult.map(tag => tag.name).join(", ");


  return (
    <TableHead>
      <TableRow>
        <TableCell align="center" colSpan={5} style={{ verticalAlign: 'middle' }}>
          <i>{tagIdsString?.toUpperCase()}</i>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell className="hidePrint" padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={someSelected}
            checked={allSelected}
            onChange={onSelectAllChange}
          />
        </TableCell>
        <SortableHeaderCell
          label={t('identifier')}
          field="key"
          sortInfo={sortInfo}
          onSortChange={onSortChange}
        />
        <SortableHeaderCell
          label={t('title')}
          field="title"
          sortInfo={sortInfo}
          onSortChange={onSortChange}
        />
        {showAdoptionDate && (
          <SortableHeaderCell
            label={t('instruments.dateOfAdoption')}
            field="adoptionDate"
            sortInfo={sortInfo}
            onSortChange={onSortChange}
          />
        )}
        {showAbrogationDate && (
          <SortableHeaderCell
            label={t('instruments.dateOfAbrogation')}
            field="abrogationDate"
            sortInfo={sortInfo}
            onSortChange={onSortChange}
          />
        )}
        {showEntryInForceDate && (
          <SortableHeaderCell
            label={t('instruments.dateOfInForce')}
            field="inForceDate"
            sortInfo={sortInfo}
            onSortChange={onSortChange}
          />
        )}
        {showLastAmendmentDate && (
          <SortableHeaderCell
            label={t('instruments.dateOfLastAmendment')}
            field="lastAmendmentDate"
            sortInfo={sortInfo}
            onSortChange={onSortChange}
          />
        )}
        {showMonitoringDate && (
          <SortableHeaderCell
            label={t('filters.monitoringYear')}
            field="monitoring"
            sortInfo={sortInfo}
            onSortChange={onSortChange}
          />
        )}
        <SortableHeaderCell
          label={t('status')}
          field="status.id"
          sortInfo={sortInfo}
          onSortChange={onSortChange}
        />
      </TableRow>
    </TableHead>
  );
};

const InstrumentsTable = (props) => {
  const {
    instruments, selected, setSelected,
    showAdoptionDate, showAbrogationDate, showLastAmendmentDate, showEntryInForceDate, showMonitoringDate,
    sortInfo, onSortChange,
  } = props;
  const auth = useAuth();
  const { lang } = useParams();
  const canEdit = auth.hasPrivilege(PRIVILEGES.editInstruments);

  const handleSelectChange = React.useCallback((iKey, checked) => {
    setSelected(currentSelected => {
      if (checked) {
        return [...currentSelected, iKey];
      } else {
        return filter(currentSelected, x => x !== iKey);
      }
    });
  }, [setSelected]);

  const onSelectAllChange = React.useCallback(() => {
    setSelected(currentSelected => {
      if (isEmpty(currentSelected)) {
        return map(instruments, 'key');
      } else {
        return [];
      }
    });
  }, [setSelected, instruments]);

  const hasSelectedItems = !isEmpty(selected);

  return (
    <TableContainer component={Paper} elevation={0}>
      <Table sx={{ minWidth: 650, width: '100%' }}>
        <InstrumentsTableHeader
          showAdoptionDate={showAdoptionDate}
          showAbrogationDate={showAbrogationDate}
          showLastAmendmentDate={showLastAmendmentDate}
          showEntryInForceDate={showEntryInForceDate}
          showMonitoringDate={showMonitoringDate}
          onSelectAllChange={onSelectAllChange}
          allSelected={!isEmpty(selected) && size(selected) === size(instruments)}
          someSelected={!isEmpty(selected) && size(selected) !== size(instruments)}
          sortInfo={sortInfo}
          onSortChange={onSortChange}

        />
        <TableBody>
          {map(instruments, (i) => {
            const iSelected = hasSelectedItems && includes(selected, i.key);
            return (
              <InstrumentsTableRow
                key={i.id}
                instrument={i}
                selected={iSelected}
                hideInPrint={hasSelectedItems && !iSelected}
                onSelectChange={handleSelectChange}
                canEdit={canEdit}
                showAdoptionDate={showAdoptionDate}
                showAbrogationDate={showAbrogationDate}
                showLastAmendmentDate={showLastAmendmentDate}
                showEntryInForceDate={showEntryInForceDate}
                showMonitoringDate={showMonitoringDate}
                lang={lang}
              />
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
};

export default InstrumentsTable;
